/**
 * PerfilBusqueda.js
 * Pagina publica de busqueda de mascota/objeto perdido
 * No requiere autenticacion - accesible via link publico
 * Incluye: datos del perdido, fotos Foto-ID, descripcion biometrica,
 *          alergias/comportamiento, recompensa, formulario de avistamiento,
 *          botones compartir, boton imprimir cartel
 *
 * Usa el LemmonTags Design System (ver src/styles/design-system.css)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../services/api/config';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../styles/design-system.css';

/**
 * Convierte URLs de proxy antiguas (/api/archivo/foto-id/...) a URLs publicas de Storage.
 * Las fotos subidas despues del fix makePublic ya tienen la URL correcta.
 */
function getPublicPhotoUrl(url) {
  if (!url) return null;
  if (url.includes('storage.googleapis.com')) return url;
  const match = url.match(/\/api\/archivo\/foto-id\/([^/]+)\/(.+)/);
  if (match) {
    const [, objetoId, filename] = match;
    const bucket = process.env.REACT_APP_ENV === 'testing'
      ? process.env.REACT_APP_STORAGE_BUCKET_TEST
      : process.env.REACT_APP_STORAGE_BUCKET_PROD;
    return `https://storage.googleapis.com/${bucket}/foto-id/${objetoId}/${filename}`;
  }
  return url;
}

export default function PerfilBusqueda() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const cartelRef = useRef(null);

  // Estado del formulario de avistamiento
  const [formNombre, setFormNombre] = useState('');
  const [formTelefono, setFormTelefono] = useState('');
  const [formMensaje, setFormMensaje] = useState('');
  const [formEnviando, setFormEnviando] = useState(false);
  const [formExito, setFormExito] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formExpandido, setFormExpandido] = useState(false);
  const [petFound, setPetFound] = useState(false); // Mascota ya encontrada
  const [objetoName, setPetName] = useState(''); // Nombre para mensaje de encontrada

  // Manage robots meta tag for SEO
  useEffect(() => {
    const setRobotsMeta = (content) => {
      let meta = document.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (loading) return; // Wait until loaded

    if (error || petFound) {
      // Inactive or found profiles should not be indexed
      setRobotsMeta('noindex, nofollow');
    } else if (data) {
      // Active profiles should be indexed
      setRobotsMeta('index, follow');
    }

    return () => {
      // Cleanup: remove robots meta on unmount
      const meta = document.querySelector('meta[name="robots"]');
      if (meta) meta.remove();
    };
  }, [loading, error, data, petFound]);

  useEffect(() => {
    async function cargarPerfil() {
      if (!id) {
        setError('Enlace inválido');
        setLoading(false);
        return;
      }
      try {
        const resp = await fetch(`${API_URL}/api/objetos/publico/${id}`);
        const json = await resp.json();
        if (!resp.ok) {
          setError(json.error || json.mensaje || 'No se pudo cargar la información');
          return;
        }
        if (!json.data) {
          setError(json.mensajePublico || 'Perfil no disponible');
          return;
        }
        // Check if profile is inactive (pet was found)
        if (!json.data.perfilPublicoBusqueda) {
          setPetName(json.data.nombre || 'La mascota');
          setPetFound(true);
          return;
        }
        setData(json.data);
      } catch (err) {
        console.error('Error cargando perfil:', err);
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    }
    cargarPerfil();
  }, [id]);

  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return null;
    try {
      const fecha = new Date(fechaStr);
      return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { return fechaStr; }
  };

  const handleCopiarLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCompartirWhatsApp = () => {
    const nombre = data?.nombre || 'mascota';
    const texto = `Ayúdame a encontrar a ${nombre}. Mira su perfil de búsqueda: ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
  };

  const handleCompartirFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  const handleImprimir = () => {
    window.print();
  };

  const getFotoUrl = () => {
    if (!data?.fotoUrl) return null;
    if (data.fotoUrl.startsWith('/api/')) {
      return `${API_URL}${data.fotoUrl}`;
    }
    return data.fotoUrl;
  };

  const getGoogleMapsUrl = (ubicacion) => {
    if (!ubicacion) return null;
    if (typeof ubicacion === 'object' && ubicacion.lat && ubicacion.lng) {
      // Usar coordenadas redondeadas para privacidad (~1km)
      const latAprox = Math.round(ubicacion.lat * 100) / 100;
      const lngAprox = Math.round(ubicacion.lng * 100) / 100;
      return `https://www.google.com/maps/search/?api=1&query=${latAprox},${lngAprox}`;
    }
    const texto = typeof ubicacion === 'object' ? ubicacion.texto : ubicacion;
    return texto ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(texto)}` : null;
  };

  const getUbicacionTexto = (ubicacion) => {
    if (!ubicacion) return null;
    return typeof ubicacion === 'object' ? ubicacion.texto : ubicacion;
  };

  const getUbicacionCoords = (ubicacion) => {
    if (typeof ubicacion === 'object' && ubicacion?.lat && ubicacion?.lng) {
      return { lat: ubicacion.lat, lng: ubicacion.lng };
    }
    return null;
  };

  const handleEnviarMensaje = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!formNombre.trim() || !formMensaje.trim()) {
      setFormError('Por favor completa tu nombre y mensaje');
      return;
    }

    setFormEnviando(true);
    try {
      const resp = await fetch(`${API_URL}/api/objetos/perdido/${id}/mensaje`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: formNombre.trim(),
          telefono: formTelefono.trim() || null,
          mensaje: formMensaje.trim(),
        }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        if (resp.status === 429) {
          setFormExito(true);
          setFormError(null);
        } else {
          setFormError(json.error || 'No se pudo enviar el mensaje');
        }
        return;
      }
      setFormExito(true);
      setFormNombre('');
      setFormTelefono('');
      setFormMensaje('');
    } catch (err) {
      console.error('Error enviando mensaje:', err);
      setFormError('Error de conexión. Intenta de nuevo.');
    } finally {
      setFormEnviando(false);
    }
  };

  // -- Loading --
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--gray-50)',
        fontFamily: 'var(--font-family-base)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: 'var(--feature-primary)', width: '48px', height: '48px' }} role="status" />
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Cargando perfil...
          </p>
        </div>
      </div>
    );
  }

  // -- Pet was found (good news!) --
  if (petFound) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--gray-50)',
        fontFamily: 'var(--font-family-base)',
        padding: 'var(--space-5)',
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '400px',
          background: 'var(--white)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-10)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--space-4)',
          }}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: '40px', color: '#22c55e' }}></i>
          </div>
          <h2 style={{
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-2)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-bold)',
          }}>
            ¡Buenas noticias!
          </h2>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-4)' }}>
            <strong>{objetoName}</strong> ya fue encontrado.
          </p>
          <p style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
            Gracias por tu interés en ayudar.
          </p>
        </div>
      </div>
    );
  }

  // -- Error --
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--gray-50)',
        fontFamily: 'var(--font-family-base)',
        padding: 'var(--space-5)',
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '400px',
          background: 'var(--white)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-10)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <i className="bi bi-search" style={{ fontSize: 'var(--font-size-4xl)', color: 'var(--gray-400)' }}></i>
          <h2 style={{
            color: 'var(--gray-900)',
            marginTop: 'var(--space-4)',
            marginBottom: 'var(--space-2)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-bold)',
          }}>
            Perfil no disponible
          </h2>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  const fotoUrl = getFotoUrl();
  const esMascota = data.tipo === 'mascota';
  const nombre = data.nombre || (esMascota ? 'Mascota' : 'Objeto');

  // Foto-ID data
  const fotoID = data.fotoID;
  const tieneFotoID = fotoID && (fotoID.fotos?.frontal || fotoID.fotos?.lateral);
  const analisis = fotoID?.analisis;

  // -- Main render --
  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; }
          .profile-container {
            box-shadow: none !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 20px !important;
          }
        }
        @media screen {
          .print-only { display: none !important; }
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--gray-50)',
        fontFamily: 'var(--font-family-base)',
        padding: 'var(--space-4)',
      }}>
        <div className="profile-container" ref={cartelRef} style={{
          maxWidth: '520px',
          margin: '0 auto',
          background: 'var(--white)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)',
        }}>

          {/* -- Header alerta (green lime) -- */}
          <div style={{
            background: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)',
            padding: 'var(--space-5) var(--space-6)',
            textAlign: 'center',
            color: 'var(--white)',
          }}>
            <div style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              marginBottom: 'var(--space-1)',
              opacity: 0.9,
            }}>
              Se busca
            </div>
            <h1 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: 800,
              margin: 0,
              letterSpacing: '-0.5px',
              fontFamily: 'var(--font-family-heading)',
            }}>
              {nombre}
            </h1>
            {esMascota && data.especie && (
              <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-1)', opacity: 0.85 }}>
                {data.especie}{data.raza ? ` - ${typeof data.raza === 'string' ? data.raza : (data.raza?.sugerida || data.raza?.razaSugerida || '')}` : ''}
              </div>
            )}
          </div>

          {/* -- Fotos Foto-ID (frontal + lateral) o foto de perfil -- */}
          {tieneFotoID ? (
            <div style={{ padding: 'var(--space-4) var(--space-6) 0' }}>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center' }}>
                {fotoID.fotos.frontal && (
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--gray-500)',
                      marginBottom: 'var(--space-1)',
                    }}>
                      <i className="bi bi-person-bounding-box me-1"></i>Frontal
                    </div>
                    <img
                      src={getPublicPhotoUrl(fotoID.fotos.frontal)}
                      alt={`Foto frontal de ${nombre}`}
                      style={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        borderRadius: 'var(--radius-lg)',
                        border: '3px solid #84cc16',
                      }}
                    />
                  </div>
                )}
                {fotoID.fotos.lateral && (
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--gray-500)',
                      marginBottom: 'var(--space-1)',
                    }}>
                      <i className="bi bi-arrow-left-right me-1"></i>Lateral
                    </div>
                    <img
                      src={getPublicPhotoUrl(fotoID.fotos.lateral)}
                      alt={`Foto lateral de ${nombre}`}
                      style={{
                        width: '100%',
                        aspectRatio: '4 / 3',
                        objectFit: 'cover',
                        borderRadius: 'var(--radius-lg)',
                        border: '3px solid #84cc16',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : fotoUrl ? (
            <div style={{ padding: 'var(--space-4) var(--space-6) 0', textAlign: 'center' }}>
              <img
                src={fotoUrl}
                alt={`Foto de ${nombre}`}
                style={{
                  width: '100%',
                  maxHeight: '320px',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-lg)',
                  border: '3px solid #84cc16',
                }}
              />
            </div>
          ) : null}

          {/* -- Descripción para cartel (prominente) -- */}
          {(analisis?.descripcionParaCartel || analisis?.descripcionGeneral) && (
            <div style={{
              margin: 'var(--space-4) var(--space-6) 0',
              padding: 'var(--space-4)',
              backgroundColor: '#f5f5f5',
              border: '1px solid #e5e5e5',
              borderRadius: 'var(--radius-md)',
            }}>
              <div style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--gray-900)',
                marginBottom: 'var(--space-2)',
              }}>
                Cómo identificarlo
              </div>
              <p style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--gray-800)',
                lineHeight: 'var(--line-height-relaxed, 1.7)',
                margin: 0,
              }}>
                {analisis.descripcionParaCartel || analisis.descripcionGeneral}
              </p>
            </div>
          )}

          {/* -- Descripcion biometrica Foto-ID -- */}
          {analisis && (
            <div style={{
              margin: 'var(--space-4) var(--space-6) 0',
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: 'var(--gray-50)',
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-md)',
            }}>
              <div style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--feature-dark)',
                marginBottom: 'var(--space-2)',
              }}>
                <i className="bi bi-fingerprint me-1"></i>
                Identificación biométrica
              </div>

              {/* Detalles biometricos en grid */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {analisis.raza && (
                  <DetailBadge icon="bi-tag" label="Raza" value={
                    typeof analisis.raza === 'string'
                      ? analisis.raza
                      : analisis.raza?.sugerida || analisis.raza?.razaSugerida || null
                  } />
                )}
                {analisis.colorPrincipal && (
                  <DetailBadge icon="bi-palette" label="Color" value={analisis.colorPrincipal} />
                )}
                {analisis.complexion && (
                  <DetailBadge icon="bi-rulers" label="Complexión" value={analisis.complexion} />
                )}
                {analisis.tipoPelaje && (
                  <DetailBadge icon="bi-brush" label="Pelaje" value={analisis.tipoPelaje} />
                )}
                {analisis.colorOjos && (
                  <DetailBadge icon="bi-eye" label="Ojos" value={analisis.colorOjos} />
                )}
                {analisis.patron && (
                  <DetailBadge icon="bi-grid-3x3" label="Patrón" value={analisis.patron} />
                )}
              </div>

              {/* Colores secundarios */}
              {Array.isArray(analisis.coloresSecundarios) && analisis.coloresSecundarios.length > 0 && (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Colores secundarios:
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginLeft: 'var(--space-1)' }}>
                    {analisis.coloresSecundarios.join(', ')}
                  </span>
                </div>
              )}

              {/* Manchas detalladas */}
              {Array.isArray(analisis.manchasDetalladas) && analisis.manchasDetalladas.length > 0 && (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Manchas/marcas:
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginLeft: 'var(--space-1)' }}>
                    {analisis.manchasDetalladas.join(', ')}
                  </span>
                </div>
              )}

              {/* Anomalias anatomicas */}
              {Array.isArray(analisis.anomaliasAnatomicas) && analisis.anomaliasAnatomicas.length > 0 && (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Rasgos distintivos:
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginLeft: 'var(--space-1)' }}>
                    {analisis.anomaliasAnatomicas.join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* -- Recompensa -- */}
          {data.ofrecerRecompensa && data.recompensa && (
            <div style={{
              margin: 'var(--space-4) var(--space-6) 0',
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: '#f5f5f5',
              border: '1.5px solid #e5e5e5',
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: '#6b6b6b',
                marginBottom: 'var(--space-1)',
              }}>
                <i className="bi bi-gift-fill me-1"></i>
                Recompensa ofrecida
              </div>
              <div style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 800,
                color: '#84cc16',
              }}>
                {data.recompensa}
              </div>
            </div>
          )}

          {/* -- Alergias y comportamiento -- */}
          {esMascota && (data.alergias || data.comportamientoConExtranos) && (
            <div style={{
              margin: 'var(--space-4) var(--space-6) 0',
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: '#f5f5f5',
              border: '1px solid #e5e5e5',
              borderRadius: 'var(--radius-md)',
            }}>
              <div style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: '#0a0a0a',
                marginBottom: 'var(--space-2)',
              }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                Información importante
              </div>
              {data.alergias && (
                <div style={{ marginBottom: data.comportamientoConExtranos ? 'var(--space-2)' : 0 }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Alergias:
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)', marginLeft: 'var(--space-1)' }}>
                    {data.alergias}
                  </span>
                </div>
              )}
              {data.comportamientoConExtranos && (
                <div>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Comportamiento con extraños:
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)', marginLeft: 'var(--space-1)' }}>
                    {data.comportamientoConExtranos}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* -- Detalles de pérdida -- */}
          <div style={{ padding: 'var(--space-4) var(--space-6)' }}>
            {data.ubicacionPerdida && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--gray-500)',
                  marginBottom: 'var(--space-1)',
                }}>
                  <i className="bi bi-geo-alt-fill me-1" style={{ color: 'var(--feature-primary)' }}></i>
                  Zona de pérdida
                </div>
                <div style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--gray-900)',
                  lineHeight: 'var(--line-height-normal)',
                }}>
                  {getUbicacionTexto(data.ubicacionPerdida)}
                </div>
                {/* Mapa estático si hay coordenadas (zona aproximada por privacidad) */}
                {(() => {
                  const coords = getUbicacionCoords(data.ubicacionPerdida);
                  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
                  if (coords && apiKey) {
                    // Redondear a 2 decimales (~1km precisión) para proteger la ubicación exacta
                    const latAprox = Math.round(coords.lat * 100) / 100;
                    const lngAprox = Math.round(coords.lng * 100) / 100;
                    return (
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${latAprox},${lngAprox}&zoom=13&size=460x200&scale=2&key=${apiKey}`}
                        alt="Zona aproximada de pérdida"
                        style={{
                          width: '100%',
                          borderRadius: 'var(--radius-md)',
                          marginTop: 'var(--space-2)',
                          border: '1px solid #e5e5e5',
                        }}
                      />
                    );
                  }
                  return null;
                })()}
                <a
                  href={getGoogleMapsUrl(data.ubicacionPerdida)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--feature-primary)',
                    fontWeight: 'var(--font-weight-semibold)',
                    textDecoration: 'none',
                    marginTop: 'var(--space-1)',
                  }}
                >
                  <i className="bi bi-map"></i> Ver en Google Maps
                </a>
              </div>
            )}
            {data.fechaPerdida && (
              <InfoRow icon="bi-calendar-event" label="Fecha de pérdida" value={formatearFecha(data.fechaPerdida)} />
            )}
            {data.queLlevabaPuesto && (
              <InfoRow icon="bi-tag-fill" label="Llevaba puesto" value={data.queLlevabaPuesto} />
            )}
            {data.dondeBuscar && (
              <InfoRow icon="bi-binoculars-fill" label="Dónde buscar" value={data.dondeBuscar} />
            )}
            {data.queProvocoEscape && (
              <InfoRow icon="bi-lightning-fill" label="Causa del escape" value={data.queProvocoEscape} />
            )}
            {data.senasParticulares && (
              <InfoRow icon="bi-bookmark-fill" label="Señas particulares" value={data.senasParticulares} />
            )}
          </div>

          {/* -- Formulario de avistamiento (colapsado por defecto) -- */}
          <div style={{
            margin: '0 var(--space-6) var(--space-4)',
            backgroundColor: 'var(--feature-light)',
            border: '1px solid var(--feature-accent)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}>
            <div
              onClick={() => !formExito && setFormExpandido(!formExpandido)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3) var(--space-4)',
                cursor: formExito ? 'default' : 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--feature-dark)',
              }}>
                <i className="bi bi-envelope-fill me-1"></i>
                {formExito ? 'Mensaje enviado' : `¿Viste a ${nombre}? Envía un mensaje al dueño`}
              </span>
              {!formExito && (
                <i className={`bi bi-chevron-${formExpandido ? 'up' : 'down'}`} style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--feature-dark)',
                }}></i>
              )}
            </div>

            {formExito ? (
              <div style={{
                textAlign: 'center',
                padding: '0 var(--space-4) var(--space-4)',
              }}>
                <i className="bi bi-check-circle-fill" style={{ fontSize: 'var(--font-size-3xl)', color: 'var(--color-success)' }}></i>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-700)',
                  marginTop: 'var(--space-2)',
                  marginBottom: 'var(--space-2)',
                }}>
                  Tu mensaje fue enviado al dueño de {nombre}. Gracias por ayudar.
                </p>
                <button
                  onClick={() => setFormExito(false)}
                  style={{
                    padding: 'var(--space-1) var(--space-3)',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--white)',
                    color: 'var(--gray-700)',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                  }}
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : formExpandido ? (
              <form onSubmit={handleEnviarMensaje} style={{ padding: '0 var(--space-4) var(--space-4)' }}>
                <div style={{ marginBottom: 'var(--space-2)' }}>
                  <input
                    type="text"
                    placeholder="Tu nombre *"
                    value={formNombre}
                    onChange={(e) => setFormNombre(e.target.value)}
                    maxLength={100}
                    required
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontFamily: 'var(--font-family-base)',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ marginBottom: 'var(--space-2)' }}>
                  <input
                    type="tel"
                    placeholder="Tu teléfono (opcional)"
                    value={formTelefono}
                    onChange={(e) => setFormTelefono(e.target.value)}
                    maxLength={30}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontFamily: 'var(--font-family-base)',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ marginBottom: 'var(--space-2)' }}>
                  <textarea
                    placeholder="¿Dónde y cuándo lo viste? Describe lo que observaste *"
                    value={formMensaje}
                    onChange={(e) => setFormMensaje(e.target.value)}
                    maxLength={1000}
                    required
                    rows={3}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontFamily: 'var(--font-family-base)',
                      outline: 'none',
                      resize: 'vertical',
                    }}
                  />
                </div>
                {formError && (
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-danger)',
                    marginBottom: 'var(--space-2)',
                  }}>
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {formError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={formEnviando}
                  style={{
                    width: '100%',
                    padding: 'var(--space-2) var(--space-4)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--feature-primary)',
                    color: 'var(--white)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    cursor: formEnviando ? 'not-allowed' : 'pointer',
                    opacity: formEnviando ? 0.7 : 1,
                    transition: 'var(--transition-fast)',
                    fontFamily: 'var(--font-family-base)',
                  }}
                >
                  {formEnviando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-1"></i>
                      Enviar mensaje al dueño
                    </>
                  )}
                </button>
              </form>
            ) : null}
          </div>

          {/* -- Botones compartir (no-print) -- */}
          <div className="no-print" style={{
            padding: '0 var(--space-6) var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}>
            <div style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--gray-600)',
              marginBottom: 'var(--space-1)',
            }}>
              <i className="bi bi-share me-1"></i>
              Ayuda a difundir
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button onClick={handleCompartirWhatsApp} style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--space-2) var(--space-3)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                backgroundColor: '#25D366',
                color: 'var(--white)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
              }}>
                <i className="bi bi-whatsapp me-1"></i> WhatsApp
              </button>
              <button onClick={handleCompartirFacebook} style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--space-2) var(--space-3)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                backgroundColor: '#1877F2',
                color: 'var(--white)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
              }}>
                <i className="bi bi-facebook me-1"></i> Facebook
              </button>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button onClick={handleCopiarLink} style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--space-2) var(--space-3)',
                border: '1.5px solid var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--white)',
                color: 'var(--gray-800)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
              }}>
                <i className={`bi ${copied ? 'bi-check-lg' : 'bi-link-45deg'} me-1`}></i>
                {copied ? 'Copiado!' : 'Copiar link'}
              </button>
              <button onClick={handleImprimir} style={{
                flex: 1,
                minWidth: '120px',
                padding: 'var(--space-2) var(--space-3)',
                border: '1.5px solid var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--white)',
                color: 'var(--gray-800)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
              }}>
                <i className="bi bi-printer me-1"></i> Imprimir cartel
              </button>
            </div>
          </div>

          {/* -- Footer -- */}
          <div style={{
            padding: 'var(--space-3) var(--space-6)',
            borderTop: '1px solid var(--gray-200)',
            textAlign: 'center',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--gray-500)',
          }}>
            🍋 Protegido por{' '}
            <a
              href="https://lemmontags.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand-primary)', fontWeight: 'var(--font-weight-semibold)', textDecoration: 'none' }}
            >
              LemmonTags.com
            </a>
            <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--font-size-2xs, 10px)', color: 'var(--gray-400)' }}>
              Sistema de identificación inteligente para mascotas
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--gray-500)',
        marginBottom: 'var(--space-1)',
      }}>
        <i className={`bi ${icon} me-1`} style={{ color: 'var(--feature-primary)' }}></i>
        {label}
      </div>
      <div style={{
        fontSize: 'var(--font-size-base)',
        color: 'var(--gray-900)',
        lineHeight: 'var(--line-height-normal)',
      }}>
        {value}
      </div>
    </div>
  );
}

function DetailBadge({ icon, label, value }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 8px',
      backgroundColor: 'var(--white)',
      border: '1px solid var(--gray-200)',
      borderRadius: 'var(--radius-full)',
      fontSize: 'var(--font-size-xs)',
      color: 'var(--gray-700)',
    }}>
      <i className={`bi ${icon}`} style={{ color: 'var(--feature-primary)', fontSize: '10px' }}></i>
      <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--gray-500)' }}>{label}:</span>
      {value}
    </span>
  );
}
